'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Typography from '@/components/primitives/typography';
import { Bell, ChevronLeft, Search } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';

import {motion} from "motion/react";
import styles from './styles.module.scss';
import Input from '@/components/primitives/input';
import Container from '@/components/primitives/container';
import { doNothing } from '@/utils/function';
import { useAuth } from '@/context/auth-context';

export interface HeaderData {
  title?: React.ReactNode | string;
  /** PC(769px 이상) 제목 줄에 쓸 제목. 없으면 title을 쓴다. */
  desktopTitle?: string;
  useBack?: boolean;
  /** false면 PC에서 뒤로 가기 버튼을 숨긴다. PC는 상단 내비로 이동하므로 상세 화면만 뒤로 가기를 둔다. */
  desktopUseBack?: boolean;
  rightElement?: () => React.ReactNode;
  searchMode?: boolean;
  searchValue?: string;
  searchKeyword?: string;
  autoFocus?: boolean,
  onSearch?: (keyword: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>, value: string) => void;
}

interface HeaderProps {
  children?: React.ReactNode;
}

interface HeaderProviderProps extends HeaderData {
  setHeader: (header: HeaderData) => void,
}

const HeaderContext = createContext<HeaderProviderProps>({
  title: '',
  useBack: true,
  rightElement: () => <></>,
  searchMode: false,
  searchValue: '',
  searchKeyword: '',
  autoFocus: false,
  onSearch: doNothing,
  setHeader: (_: HeaderData) => {
  },
});

const bounceTransition = {
  type: "spring",
  stiffness: 400,
  damping: 17
} as const;

const HeaderBackButton = ({ desktopHidden = false }: { desktopHidden?: boolean }) => {
  const router = useRouter();

  return <Container className={`${styles.back} ${styles.transitionButton}`} rootClassName={`${styles.iconRoot} focus-ring ${desktopHidden ? styles.desktopHidden : ''}`} blur={true}>
    <button type="button" className={styles.iconButton} aria-label="뒤로 가기" onClick={router.back}>
      <ChevronLeft
        width={28}
        height={28}
        strokeWidth={1}
      />
    </button>
  </Container>;
};

// PC는 상단 내비에 알림이 있으므로 모바일에서만 보인다.
const HeaderNotificationButton = () => (
  <Link href="/notifications" aria-label="알림" className={`focus-ring ${styles.desktopHidden}`}>
    <Container className={`${styles.notification} ${styles.transitionButton}`} rootClassName={styles.iconRoot}
               blur={true}>
      <Bell width={22} height={22} strokeWidth={1.25} />
    </Container>
  </Link>
);

const HeaderProvider = (props: HeaderProps) => {
  const { children } = props;
  const auth = useAuth();
  const path = usePathname();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [header, setHeader] = useState<HeaderData>({
    title: '',
    useBack: true,
    rightElement: () => <></>,
    searchMode: false,
    searchValue: '',
    searchKeyword: '',
    autoFocus: false,
    onSearch: () => {
    },
  });
  const {
    title = '',
    desktopTitle,
    useBack = true,
    desktopUseBack = true,
    rightElement,
    searchMode = false,
    searchValue = '',
    autoFocus = false,
    onSearch = doNothing,
    onFocus = doNothing,
    onBlur = doNothing,
    onKeyDown = doNothing,
  } = header;
  const [searchKeyword, setSearchKeyword] = useState(searchValue);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    onSearch(searchKeyword);

    if(!inputRef.current) return;

    inputRef.current.blur();
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  }, []);

  useEffect(() => {
    setSearchKeyword(searchValue);
  }, [searchValue]);

  const right = rightElement?.call(null);

  return <HeaderContext.Provider value={{
    title,
    desktopTitle,
    useBack,
    desktopUseBack,
    rightElement,
    searchMode,
    searchValue,
    searchKeyword,
    autoFocus,
    onSearch,
    onFocus,
    onBlur,
    onKeyDown,
    setHeader,
  }}>
    <header ref={rootRef}
            className={styles.container}>
      <div className={styles.cap} />
      <div className={styles.headerStack}>
        {searchMode ? (
          <div className={styles.searcdiv}>
            <Typography className={styles.desktopSearchTitle} size="large" weight="bold">
              {desktopTitle || '검색'}
            </Typography>
            {useBack ? <HeaderBackButton desktopHidden={!desktopUseBack} /> : null}
            <form className={styles.form} onSubmit={handleSearch}>
              <Input
                ref={inputRef}
                placeholder="검색어를 입력하세요"
                onChange={handleInputChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={(event) => onKeyDown(event, searchKeyword)}
                autoFocus={autoFocus}
                value={searchKeyword}
                maxLength={250}
                right={
                  <button type="submit" aria-label="검색">
                    <Search width={20} height={20} color="currentColor" aria-hidden="true" />
                  </button>
                }
              />
              <button type="submit" className={styles.desktopSubmit}>검색</button>
            </form>
          </div>
        ) : (
          <>
            <div className={styles.normalStack}>
              {useBack ? <HeaderBackButton desktopHidden={!desktopUseBack} /> : null}
              <div className={`${styles.title} blur`}>
                <Typography className={styles.titleText} size="large" weight="bold">
                  <motion.div
                    key={path}
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 10, opacity: 0 }}
                    transition={{
                      y: bounceTransition,
                      opacity: { duration: 0.2 }
                    }}
                  >
                    <span className={styles.mobileTitle}>{title || '로네'}</span>
                    <span className={styles.desktopTitle}>{desktopTitle || title || '로네'}</span>
                  </motion.div>
                </Typography>
              </div>

              {auth.me ? (
                <HeaderNotificationButton />
              ) : null}

              {!!right ? <Container className={styles.rightElement} rootClassName={styles.iconRoot} blur={true}>
                {right}
              </Container> : ''}
            </div>
          </>
        )}
      </div>
    </header>
    <div className={styles.children}>
      {children}
    </div>
  </HeaderContext.Provider>;
};

export const useHeader = (header: HeaderData) => {
  const context = useContext(HeaderContext);
  const {
    title,
    desktopTitle,
    useBack,
    desktopUseBack,
    rightElement,
    searchMode = false,
    searchValue,
    onSearch,
    onFocus,
    onBlur,
    onKeyDown,
  } = header;

  useEffect(() => {
    context.setHeader(header);
    return () => {
      context.setHeader({
        title: '',
        useBack: true,
        rightElement: () => <></>,
        searchMode: false,
        searchValue: '',
        searchKeyword: '',
        onSearch: doNothing,
        onFocus: doNothing,
        onBlur: doNothing,
        onKeyDown: doNothing,
      });
    };
  }, [title, desktopTitle, useBack, desktopUseBack, rightElement, searchMode, searchValue, onSearch, onFocus, onBlur, onKeyDown]);

  return context;
};

export default HeaderProvider;
